import {
  ClipboardCheckIcon, FlagIcon, LayersIcon, UserPlusIcon, WebhookIcon,
} from 'lucide-react';

import type { StringKey } from '@/lib/strings/en';

/**
 * Notification feed — THE SHAPE, waiting on an endpoint.
 *
 * There is no notifications API. Nothing in the contract publishes one, and the
 * events that will feed it (`result.finalized` webhook deliveries, QC flags,
 * plan publications — see build/10 §7) are only emitted server-side.
 *
 * `AppNotification` is what that endpoint must return, except that `time`
 * becomes an ISO `createdAt` formatted at the edge.
 *
 * Two deliberate departures from the kit's version of this data:
 *   - no remote avatar URLs (the kit points at i.pravatar.cc). The platform is
 *     self-hosted with no public egress assumptions (PRD NFR "portable,
 *     self-hosted"), so identity renders as initials.
 *   - kind colours come from theme tokens, not `bg-blue-500`, so the feed
 *     re-skins with the eight presets and in dark mode.
 */

export type NotificationKind = 'result' | 'webhook' | 'quality' | 'plan' | 'access';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  description: string;
  /** An ISO `createdAt` once the API exists; formatted at the edge. */
  time: string;
  unread: boolean;
  /** Present when a person triggered it — rendered as initials, never a photo. */
  actor?: { name: string; initials: string };
  /** `access` items are actionable; set once Accept/Decline is pressed. */
  requiresDecision?: boolean;
  decision?: 'accepted' | 'declined';
}

export const KIND_META: Record<
  NotificationKind,
  { label: StringKey; icon: typeof LayersIcon; tone: 'default' | 'danger' }
> = {
  result: { label: 'notifications.kind.result', icon: ClipboardCheckIcon, tone: 'default' },
  webhook: { label: 'notifications.kind.webhook', icon: WebhookIcon, tone: 'danger' },
  quality: { label: 'notifications.kind.quality', icon: FlagIcon, tone: 'danger' },
  plan: { label: 'notifications.kind.plan', icon: LayersIcon, tone: 'default' },
  access: { label: 'notifications.kind.access', icon: UserPlusIcon, tone: 'default' },
};

/**
 * THERE IS NO SAMPLE FEED ANY MORE, AND THAT IS THE FIX.
 *
 * Six invented rows used to live here — a failed webhook, an access request, a
 * batch re-score — labelled "Sample data" on both surfaces that rendered them.
 * The label was the compromise, and it did not work: the first operator to look
 * at the bell reported the notifications as broken, because a feed showing
 * plausible events that no action ever changes IS broken as far as a reader can
 * tell. Labelling fabricated data does not stop it being read as data.
 *
 * This codebase already knows the answer — `pages/screens.tsx` records it after
 * five failures with phase-labelled placeholders: **say what is not built and
 * what it is waiting on.** So both surfaces now render their real empty state,
 * the bell carries no unread dot, and the page says the feed is waiting on a
 * read API over the audit trail (`10 §7`).
 *
 * `AppNotification` and `KIND_META` stay: they are the shape that endpoint must
 * return, and the popup and page are built against them. When it lands, the
 * store fetches instead of holding an empty array and nothing else changes.
 */
export const NOTIFICATION_SAMPLES: AppNotification[] = [];
